import { useState, useCallback, type FormEvent } from 'react';
import { useAuth, useClerk } from '@clerk/react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { ArrowRight, Loader2 } from 'lucide-react';
import { AuthShell } from '../../components/ui/auth-shell/auth-shell.js';
import { SignUpForm } from './sign-up-form.js';
import { GoogleIcon } from './google-icon.js';

export function SignUpPage() {
  const { t } = useTranslation('common');
  const { isLoaded, isSignedIn } = useAuth();
  const clerk = useClerk();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [validated, setValidated] = useState(false);
  const [googleInFlight, setGoogleInFlight] = useState(false);

  const handlePreCheck = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError('');

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError(t('auth.signup.validationError', 'Please enter a valid email address.'));
        return;
      }

      setChecking(true);
      try {
        const baseUrl = import.meta.env.VITE_API_BASE || '/api';
        const res = await fetch(`${baseUrl}/v1/auth/signup-check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });

        if (res.status === 403) {
          setError(
            t(
              'auth.signup.disposableEmailError',
              'Please use a permanent email address. Disposable email services are not supported.',
            ),
          );
          return;
        }
        if (res.status === 429) {
          setError(
            t(
              'auth.signup.ipLimitError',
              'Too many accounts created from this network. Please try again tomorrow.',
            ),
          );
          return;
        }
        if (!res.ok) {
          // Non-blocking: allow signup to proceed if check fails unexpectedly
          setValidated(true);
          return;
        }

        setValidated(true);
      } catch {
        // Fail-open: if API unreachable, allow signup
        setValidated(true);
      } finally {
        setChecking(false);
      }
    },
    [email, t],
  );

  const handleGoogle = useCallback(async () => {
    if (googleInFlight) return;
    setError('');
    // Google sign-up intentionally bypasses the disposable-email precheck: an
    // OAuth Google account carries a verified, non-disposable email, so the spam
    // gate (which only matters for password sign-ups) does not apply. Uses the
    // stable signUp resource off the Clerk client (see sign-in-form.tsx).
    const stableSignUp = clerk.client?.signUp;
    if (!stableSignUp) {
      console.warn('[auth] Google sign-up clicked before Clerk SDK ready');
      setError(
        t(
          'auth.signup.errors.notReady',
          'Sign-up is still initializing. Please try again in a moment.',
        ),
      );
      return;
    }
    setGoogleInFlight(true);
    const watchdog = window.setTimeout(() => {
      setGoogleInFlight(false);
      setError(
        t('auth.signup.errors.stalled', "Google didn't open. Wait a moment, then try again."),
      );
    }, 6000);
    try {
      await stableSignUp.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: `${window.location.origin}/sso-callback`,
        redirectUrlComplete: `${window.location.origin}/getting-started`,
      });
    } catch (err) {
      console.error('[auth] signUp.authenticateWithRedirect threw', err);
      setError(
        t(
          'auth.signup.errors.googleFailed',
          'Google sign up failed. Check your connection and try again.',
        ),
      );
    } finally {
      window.clearTimeout(watchdog);
      setGoogleInFlight(false);
    }
  }, [clerk, t, googleInFlight]);

  const footer = (
    <span>
      {t('auth.signup.hasAccount', 'Already have an account?')}{' '}
      <a href="/sign-in" className="text-accent-cyan transition-colors hover:underline">
        {t('auth.signup.signIn', 'Sign in')}
      </a>
    </span>
  );

  if (isLoaded && isSignedIn) {
    return <Navigate to="/dashboard" replace />;
  }

  const ariaLabel = t('auth.signup.ariaLabel', 'Sign up');
  const eyebrow = t('auth.signup.eyebrow', 'Create your account');
  const title = (
    <>
      {t('auth.signup.title.a', 'Give your agent')}{' '}
      <span className="text-accent-lime">{t('auth.signup.title.accent', 'visual taste')}</span>
    </>
  );

  if (validated) {
    return (
      <AuthShell
        data-testid="auth-sign-up-page"
        ariaLabel={ariaLabel}
        eyebrow={eyebrow}
        title={title}
        footer={footer}
      >
        <SignUpForm seedEmail={email} />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      data-testid="auth-sign-up-page"
      ariaLabel={ariaLabel}
      eyebrow={eyebrow}
      title={title}
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        <button
          type="button"
          onClick={handleGoogle}
          disabled={googleInFlight}
          data-testid="signup-google"
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 text-small font-medium text-primary transition-colors hover:border-white/20 hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 disabled:opacity-60"
        >
          {googleInFlight ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <GoogleIcon className="h-4 w-4" />
          )}
          {t('auth.signup.google', 'Continue with Google')}
        </button>

        <div className="relative flex items-center">
          <span className="h-px flex-1 bg-white/[0.08]" />
          <span className="px-3 font-mono text-caption uppercase tracking-[0.15em] text-muted">
            {t('auth.signin.or', 'Or continue with')}
          </span>
          <span className="h-px flex-1 bg-white/[0.08]" />
        </div>

        <form
          onSubmit={handlePreCheck}
          className="flex flex-col gap-4"
          data-testid="signup-precheck-form"
        >
          <div className="flex flex-col gap-2">
            <label htmlFor="signup-email" className="text-small font-medium text-secondary">
              {t('auth.signup.emailLabel', 'Email address')}
            </label>
            <input
              id="signup-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.signup.emailPlaceholder', 'you@example.com')}
              className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-small text-primary placeholder:text-muted transition-colors focus:border-accent-lime/70 focus:bg-accent-lime/[0.06] focus:outline-none"
            />
          </div>

          {error && (
            <p
              className="rounded-lg border border-accent-red/40 bg-accent-red/[0.08] px-3 py-2 text-caption text-primary"
              role="alert"
              data-testid="signup-precheck-error"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={checking}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-accent-lime px-4 text-small font-semibold text-canvas transition-[filter,transform] duration-150 hover:brightness-105 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 disabled:opacity-60"
          >
            {checking
              ? t('auth.signup.checking', 'Checking…')
              : t('auth.signup.continue', 'Continue')}
            {!checking && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
          </button>

          <p className="text-caption text-muted">
            {t(
              'auth.signup.precheckHint',
              "We'll check your email before you create an account to keep AgentBrush spam-free.",
            )}
          </p>
        </form>
      </div>
    </AuthShell>
  );
}
